/**
 * Face-mask overlays for the camera composite pipeline: the preset list, the mask
 * artwork (SVG path data rasterized once to an offscreen sprite canvas), the anchor
 * math from canonical FaceLandmarker indices, temporal landmark smoothing (EMA), the
 * low-tier inference throttle and the no-face fade-out.
 *
 * Canonical coordinate system: the anchor sits at the origin, one canonical unit is
 * one hundredth of the eye-line distance (eye outer corners 33/263 sit at x = ±50),
 * x grows to the right and y grows DOWN (image space, like the landmark output).
 * Every mask is drawn from code as flat shapes in the app's zinc/white/emerald
 * vocabulary - deliberately neutral: no flags, no slogans, no brand marks, no
 * emoji art.
 *
 * PRIVACY: everything here is pure geometry over landmark coordinates that never
 * leave the page (the landmarker runs locally, see faceLandmarkerEngine.ts).
 */

/** Mask presets, in UI order («Нет / Очки / Усы / Шляпа / Корона»). */
export const FACE_MASKS = ['none', 'glasses', 'mustache', 'hat', 'crown'] as const;

/** One face-mask preset id. */
export type FaceMaskId = (typeof FACE_MASKS)[number];

/** A mask that draws something (kept for readability at use sites). */
export type ActiveFaceMask = Exclude<FaceMaskId, 'none'>;

/** Canonical units per eye distance: mask geometry is authored against eye distance = 100. */
export const CANONICAL_EYE_DISTANCE = 100;

/**
 * Canonical FaceLandmarker indices (the 478-point canonical face mesh): the eye
 * line (outer corners, for position/scale/roll), the nose tip (mustache anchor)
 * and the face-oval top (forehead line, hat and crown anchor).
 */
export const LANDMARK_EYE_OUTER_LEFT = 33;
export const LANDMARK_EYE_OUTER_RIGHT = 263;
export const LANDMARK_NOSE_TIP = 1;
export const LANDMARK_FOREHEAD_TOP = 10;

/** One normalized landmark coordinate (0..1 relative to the video frame). */
export interface NormalizedPoint {
  readonly x: number;
  readonly y: number;
}

/** The landmark list FaceLandmarker delivers for one face (may be shorter than canonical). */
export type FaceLandmarkList = readonly NormalizedPoint[];

/**
 * Face geometry in output-canvas pixels, derived from one landmark frame: the eye
 * midpoint and distance (position + scale basis), the roll (from the eye line) and
 * the pixel positions of the other anchors (nose tip, forehead top).
 */
export interface FacePose {
  readonly eyeMidX: number;
  readonly eyeMidY: number;
  readonly eyeDistance: number;
  /** Eye-line angle in radians, atan2 space (-pi..pi). */
  readonly roll: number;
  readonly noseTipX: number;
  readonly noseTipY: number;
  readonly foreheadTopX: number;
  readonly foreheadTopY: number;
}

/**
 * Computes the face pose from one landmark frame at the given output-canvas size.
 * Returns null when the canonical anchors are missing or degenerate (no usable
 * eye line) - the caller keeps the previous pose and lets the fade-out decide.
 */
export function facePoseFromLandmarks(
  landmarks: FaceLandmarkList,
  width: number,
  height: number,
): FacePose | null {
  const eyeA = landmarks[LANDMARK_EYE_OUTER_LEFT];
  const eyeB = landmarks[LANDMARK_EYE_OUTER_RIGHT];
  const nose = landmarks[LANDMARK_NOSE_TIP];
  const forehead = landmarks[LANDMARK_FOREHEAD_TOP];
  if (
    eyeA === undefined ||
    eyeB === undefined ||
    nose === undefined ||
    forehead === undefined ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  const ax = eyeA.x * width;
  const ay = eyeA.y * height;
  const bx = eyeB.x * width;
  const by = eyeB.y * height;
  const dx = bx - ax;
  const dy = by - ay;
  const eyeDistance = Math.hypot(dx, dy);
  if (!(eyeDistance > 0)) {
    return null;
  }
  return {
    eyeMidX: (ax + bx) / 2,
    eyeMidY: (ay + by) / 2,
    eyeDistance,
    roll: Math.atan2(dy, dx),
    noseTipX: nose.x * width,
    noseTipY: nose.y * height,
    foreheadTopX: forehead.x * width,
    foreheadTopY: forehead.y * height,
  };
}

/** Temporally smoothed face pose: every field is an exponential moving average. */
export type SmoothedFace = FacePose;

/**
 * EMA weight of the NEW sample at the "high" quality tier (landmarks every frame;
 * a light hand keeps the mask tracking while damping landmark jitter).
 */
export const SMOOTH_ALPHA_HIGH = 0.4;

/**
 * EMA weight of the NEW sample at the "low" tier: inference runs on every other
 * frame (see {@link shouldDetectLandmarks}), so each update carries more of the
 * new measurement to keep the average latency comparable to the high tier.
 */
export const SMOOTH_ALPHA_LOW = 0.6;

/** Smoothing weight for the tier: the new-sample EMA alpha. */
export function landmarkSmoothingAlpha(tier: 'low' | 'high'): number {
  return tier === 'high' ? SMOOTH_ALPHA_HIGH : SMOOTH_ALPHA_LOW;
}

/**
 * Blends one fresh pose into the smoothed state (`prev === null` adopts it as-is).
 * Plain per-field EMA: roll stays in atan2 space and the eye line never flips
 * sign between consecutive frames of a real face, so no angle unwrapping needed.
 */
export function smoothFace(prev: SmoothedFace | null, next: FacePose, alpha: number): SmoothedFace {
  if (prev === null) {
    return { ...next };
  }
  const mix = (a: number, b: number): number => a + (b - a) * alpha;
  return {
    eyeMidX: mix(prev.eyeMidX, next.eyeMidX),
    eyeMidY: mix(prev.eyeMidY, next.eyeMidY),
    eyeDistance: mix(prev.eyeDistance, next.eyeDistance),
    roll: mix(prev.roll, next.roll),
    noseTipX: mix(prev.noseTipX, next.noseTipX),
    noseTipY: mix(prev.noseTipY, next.noseTipY),
    foreheadTopX: mix(prev.foreheadTopX, next.foreheadTopX),
    foreheadTopY: mix(prev.foreheadTopY, next.foreheadTopY),
  };
}

/**
 * Low-tier landmark throttle: inference every other compositing tick (the drawn
 * mask rides the last smoothed pose in between - the EMA interpolation above is
 * the in-between representation). The high tier detects on every tick.
 */
export const LANDMARK_EVERY_NTH_LOW_TIER = 2;

/** True when this compositing tick should run landmark inference for the tier. */
export function shouldDetectLandmarks(tick: number, tier: 'low' | 'high'): boolean {
  return tier === 'high' || tick % LANDMARK_EVERY_NTH_LOW_TIER === 0;
}

/** No-face fade-out: the mask fades over this long after the last detected face, then stops drawing. */
export const FACE_FADE_OUT_MS = 500;

/**
 * Mask opacity for the time since the last detected face: 1 while the face is
 * fresh, fading linearly to 0 across {@link FACE_FADE_OUT_MS}, then 0 (the caller
 * skips drawing entirely at 0 - no per-frame draw cost once the face is gone).
 */
export function maskAlpha(nowMs: number, lastFaceAtMs: number): number {
  const since = nowMs - lastFaceAtMs;
  if (since <= 0) {
    return 1;
  }
  if (since >= FACE_FADE_OUT_MS) {
    return 0;
  }
  return 1 - since / FACE_FADE_OUT_MS;
}

/** One flat shape of a mask, in canonical units: SVG path data plus paint style. */
export interface MaskShape {
  readonly path: string;
  readonly fill?: string | undefined;
  readonly stroke?: string | undefined;
  readonly lineWidth?: number | undefined;
}

/** Canonical-space extent of a mask (padding included), for sprite rasterization. */
export interface MaskBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** One mask definition: the artwork plus where it anchors on the face. */
export interface MaskDefinition {
  readonly bounds: MaskBounds;
  readonly shapes: readonly MaskShape[];
  readonly anchor: (face: FacePose) => { readonly x: number; readonly y: number };
}

/** Mask palette: the app's zinc/white/emerald vocabulary, flat fills only. */
const MASK_COLORS = {
  frame: '#18181b', // zinc-900: glasses frames, hat brim
  dark: '#3f3f46', // zinc-700: mustache, hat dome
  mid: '#27272a', // zinc-800
  emerald: '#34d399', // emerald-400: hat band, crown body
  emeraldDeep: '#10b981', // emerald-500: crown base band
  lens: 'rgba(255, 255, 255, 0.14)', // white at 14%: the lens glass
} as const;

/** Builds rounded-rect SVG path data centered at (cx, cy). */
function roundedRectPath(cx: number, cy: number, w: number, h: number, r: number): string {
  const x0 = cx - w / 2;
  const y0 = cy - h / 2;
  const x1 = cx + w / 2;
  const y1 = cy + h / 2;
  return (
    `M ${x0 + r} ${y0} H ${x1 - r} A ${r} ${r} 0 0 1 ${x1} ${y0 + r} V ${y1 - r} ` +
    `A ${r} ${r} 0 0 1 ${x1 - r} ${y1} H ${x0 + r} A ${r} ${r} 0 0 1 ${x0} ${y1 - r} ` +
    `V ${y0 + r} A ${r} ${r} 0 0 1 ${x0 + r} ${y0} Z`
  );
}

/**
 * «Очки»: two rounded lenses on the eye line (eye outer corners at x = ±50 sit
 * inside the lenses), a bridge over the nose and short temple arms. Frames in
 * zinc-900, glass a faint white wash.
 */
const GLASSES: MaskDefinition = {
  bounds: { minX: -112, minY: -26, maxX: 112, maxY: 30 },
  shapes: [
    {
      path: roundedRectPath(-40, 2, 56, 40, 14),
      fill: MASK_COLORS.lens,
      stroke: MASK_COLORS.frame,
      lineWidth: 7,
    },
    {
      path: roundedRectPath(40, 2, 56, 40, 14),
      fill: MASK_COLORS.lens,
      stroke: MASK_COLORS.frame,
      lineWidth: 7,
    },
    { path: 'M -12 2 H 12', stroke: MASK_COLORS.frame, lineWidth: 7 },
    { path: 'M -68 2 H -104', stroke: MASK_COLORS.frame, lineWidth: 7 },
    { path: 'M 68 2 H 104', stroke: MASK_COLORS.frame, lineWidth: 7 },
  ],
  anchor: (face) => ({ x: face.eyeMidX, y: face.eyeMidY }),
};

/**
 * «Усы»: a symmetric handlebar under the nose, anchored at the nose tip. Flat
 * zinc-700 fill, no outline - reads at publish resolution without harsh edges.
 */
const MUSTACHE: MaskDefinition = {
  bounds: { minX: -54, minY: -10, maxX: 54, maxY: 20 },
  shapes: [
    {
      path:
        'M 0 2 C 14 -4 34 -4 46 6 C 48 8 48 10 46 11 C 34 17 16 15 6 10 ' +
        'C 2 8 -2 8 -6 10 C -16 15 -34 17 -46 11 C -48 10 -48 8 -46 6 ' +
        'C -34 -4 -14 -4 0 2 Z',
      fill: MASK_COLORS.dark,
    },
  ],
  anchor: (face) => ({ x: face.noseTipX, y: face.noseTipY }),
};

/**
 * «Шляпа»: a flat fedora-ish silhouette anchored at the face-oval top (the
 * forehead line): zinc-800 brim, zinc-700 dome, one emerald band - the only
 * color accent, from the app's own palette.
 */
const HAT: MaskDefinition = {
  bounds: { minX: -108, minY: -100, maxX: 108, maxY: 12 },
  shapes: [
    {
      path: 'M -64 -8 L -64 -58 Q -64 -96 0 -96 Q 64 -96 64 -58 L 64 -8 Z',
      fill: MASK_COLORS.dark,
    },
    { path: roundedRectPath(0, -22, 128, 16, 4), fill: MASK_COLORS.emerald },
    { path: roundedRectPath(0, -2, 208, 22, 11), fill: MASK_COLORS.mid },
  ],
  anchor: (face) => ({ x: face.foreheadTopX, y: face.foreheadTopY }),
};

/**
 * «Корона»: a three-point crown sitting just above the forehead line, emerald
 * body with a deeper emerald base band - flat, symmetric, brand-free.
 */
const CROWN: MaskDefinition = {
  bounds: { minX: -62, minY: -66, maxX: 62, maxY: 2 },
  shapes: [
    {
      path: 'M -56 -4 L -56 -52 L -28 -30 L 0 -62 L 28 -30 L 56 -52 L 56 -4 Z',
      fill: MASK_COLORS.emerald,
    },
    { path: roundedRectPath(0, -14, 116, 14, 4), fill: MASK_COLORS.emeraldDeep },
  ],
  anchor: (face) => ({ x: face.foreheadTopX, y: face.foreheadTopY }),
};

/** Mask artwork per preset id. */
export const MASK_DEFINITIONS: Readonly<Record<ActiveFaceMask, MaskDefinition>> = {
  glasses: GLASSES,
  mustache: MUSTACHE,
  hat: HAT,
  crown: CROWN,
};

/** The definition of one active mask (id already narrowed by the caller). */
export function maskDefinitionOf(id: ActiveFaceMask): MaskDefinition {
  return MASK_DEFINITIONS[id];
}

/**
 * One rasterized mask sprite: the offscreen canvas plus the canonical-unit offset
 * of the anchor point from the sprite's top-left corner (the renderer draws in
 * canonical space after the pose transform, so offsets are canonical, not sprite
 * pixels) and the sprite resolution (sprite pixels per canonical unit) the
 * renderer divides the natural size by when compositing.
 */
export interface MaskSprite {
  readonly canvas: HTMLCanvasElement;
  readonly originX: number;
  readonly originY: number;
  readonly pixelRatio: number;
}

/** All four mask sprites, rasterized once per processor start. */
export type MaskSpriteSet = Readonly<Record<ActiveFaceMask, MaskSprite>>;

/** Sprite resolution: sprite pixels per canonical unit (2 = retina-ish crispness). */
export const MASK_SPRITE_SCALE = 2;

/**
 * Rasterizes one mask definition into an offscreen sprite canvas. Browser-only
 * (Path2D): the production wiring lives in segmentationEngine.ts; jsdom unit
 * tests exercise the geometry above, never this function.
 */
export function rasterizeMask(
  definition: MaskDefinition,
  createCanvas: (width: number, height: number) => HTMLCanvasElement,
  scale: number = MASK_SPRITE_SCALE,
): MaskSprite {
  const width = Math.ceil((definition.bounds.maxX - definition.bounds.minX) * scale);
  const height = Math.ceil((definition.bounds.maxY - definition.bounds.minY) * scale);
  const canvas = createCanvas(Math.max(1, width), Math.max(1, height));
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('canvas 2d context is unavailable');
  }
  ctx.translate(-definition.bounds.minX * scale, -definition.bounds.minY * scale);
  ctx.scale(scale, scale);
  for (const shape of definition.shapes) {
    const path = new Path2D(shape.path);
    if (shape.fill !== undefined) {
      ctx.fillStyle = shape.fill;
      ctx.fill(path);
    }
    if (shape.stroke !== undefined) {
      ctx.strokeStyle = shape.stroke;
      ctx.lineWidth = shape.lineWidth ?? 1;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(path);
    }
  }
  return {
    canvas,
    originX: -definition.bounds.minX,
    originY: -definition.bounds.minY,
    pixelRatio: scale,
  };
}

/** Rasterizes the full sprite set (once per processor start that can show masks). */
export function rasterizeMaskSet(
  createCanvas: (width: number, height: number) => HTMLCanvasElement,
): MaskSpriteSet {
  return {
    glasses: rasterizeMask(GLASSES, createCanvas),
    mustache: rasterizeMask(MUSTACHE, createCanvas),
    hat: rasterizeMask(HAT, createCanvas),
    crown: rasterizeMask(CROWN, createCanvas),
  };
}
